import { useCallback } from 'react';
import axios from 'axios';
import clientService from '../clientService';
import { FileAdminDetailWithUser } from '../api/Api';

// Max concurrent uploads to GCS at a time
const MAX_CONCURRENT_UPLOADS = 5;

// Map file extensions to MIME types
const EXTENSION_TO_MIME: Record<string, string> = {
  wav: 'audio/wav',
  mp3: 'audio/mpeg',
  flac: 'audio/flac',
  ogg: 'audio/ogg',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  mp4: 'video/mp4',
  avi: 'video/x-msvideo',
  mov: 'video/quicktime',
};

// Resolve content type from file extension
function resolveContentType(file: File): string {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_TO_MIME[ext] || 'application/octet-stream';
}

export interface UploadFileItem {
  file: File;
  id: string;
}

export interface UploadResult {
  id: string;
  file: File;
  fileRecord: FileAdminDetailWithUser | null;
  error?: string;
}

export type UploadPhase =
  | 'idle'
  | 'preparing'
  | 'uploading'
  | 'finalizing'
  | 'done';

export interface BatchProgress {
  phase: UploadPhase;
  uploadedCount: number;
  totalCount: number;
  failedCount: number;
  overallPct: number;
}

interface UseFileUploadResult {
  uploadFiles: (
    items: UploadFileItem[],
    deploymentId: string,
    onBatchProgress: (progress: BatchProgress) => void,
  ) => Promise<UploadResult[]>;
}

function createSemaphore(limit: number) {
  let active = 0;
  const queue: (() => void)[] = [];

  const acquire = (): Promise<void> =>
    new Promise((resolve) => {
      if (active < limit) { active++; resolve(); }
      else { queue.push(() => { active++; resolve(); }); }
    });

  const release = () => {
    active--;
    const next = queue.shift();
    if (next) next();
  };

  return { acquire, release };
}

const useFileUpload = (): UseFileUploadResult => {
  const uploadFiles = useCallback(
    async (
      items: UploadFileItem[],
      deploymentId: string,
      onBatchProgress: (progress: BatchProgress) => void,
    ): Promise<UploadResult[]> => {
      if (items.length === 0) return [];

      const totalCount = items.length;
      const totalBytes = items.reduce((sum, i) => sum + i.file.size, 0);

      const bytesUploadedPerFile = new Map<string, number>(
        items.map((i) => [i.id, 0]),
      );
      let uploadedCount = 0;
      let failedCount = 0;

      const emitProgress = (phase: UploadPhase) => {
        const totalUploaded = [...bytesUploadedPerFile.values()].reduce((a, b) => a + b, 0);
        const overallPct = totalBytes > 0 ? Math.round((totalUploaded / totalBytes) * 100) : 0;
        onBatchProgress({ phase, uploadedCount, totalCount, failedCount, overallPct });
      };

      
      onBatchProgress({
        phase: 'preparing',
        uploadedCount: 0,
        totalCount,
        failedCount: 0,
        overallPct: 0,
      });

      const contentTypeByFileName: Record<string, string> = {};

      const urlMap: Record<string, { upload_url: string; gcs_path: string }> = {};

      try {
        const batchUrlResponse =
          await clientService.api.batchGenerateUploadUrlsApiV1AdminFilesGenerateUploadUrlsPost({
            deployment_id: deploymentId,
            files: items.map((item) => {
              const contentType = resolveContentType(item.file);
              contentTypeByFileName[item.file.name] = contentType;
              return {
                file_name: item.file.name,
                content_type: contentType,
              };
            }),
          });

        for (const entry of batchUrlResponse.data) {
          urlMap[entry.file_name] = {
            upload_url: entry.upload_url,
            gcs_path: entry.gcs_path,
          };
        }
      } catch (err) {
        console.error('Batch generate upload URLs failed:', err);
        onBatchProgress({
          phase: 'done',
          uploadedCount: 0,
          totalCount,
          failedCount: totalCount,
          overallPct: 0,
        });
        return items.map((item) => ({
          id: item.id,
          file: item.file,
          fileRecord: null,
          error: 'Failed to get upload URLs from server',
        }));
      }

      emitProgress('uploading');

      const semaphore = createSemaphore(MAX_CONCURRENT_UPLOADS);

      const gcsResults: Array<{
        id: string;
        file: File;
        gcs_path: string | null;
        error?: string;
      }> = await Promise.all(
        items.map(async (item) => {
          const urlEntry = urlMap[item.file.name];
          if (!urlEntry) {
            failedCount++;
            bytesUploadedPerFile.set(item.id, item.file.size);
            emitProgress('uploading');
            return {
              id: item.id,
              file: item.file,
              gcs_path: null,
              error: 'No signed URL received for this file',
            };
          }

          const contentType =
            contentTypeByFileName[item.file.name] ?? resolveContentType(item.file);

          await semaphore.acquire();
          try {
            let attempt = 0;
            let success = false;
            while (attempt < 3 && !success) {
              try {
                await axios.put(urlEntry.upload_url, item.file, {
                  headers: { 'Content-Type': contentType },
                  onUploadProgress: (progressEvent) => {
                    if (progressEvent.loaded !== undefined) {
                      bytesUploadedPerFile.set(item.id, progressEvent.loaded);
                      emitProgress('uploading');
                    }
                  },
                });
                success = true;
              } catch (err: any) {
                attempt++;
                if (attempt < 3 && (!err.response || err.response.status >= 500)) {
                  // exponential backoff with jitter
                  const delay = Math.min(5000, 1000 * Math.pow(2, attempt) + Math.random() * 1000);
                  await new Promise((res) => setTimeout(res, delay));
                } else {
                  throw err;
                }
              }
            }

            bytesUploadedPerFile.set(item.id, item.file.size);
            uploadedCount++;
            emitProgress('uploading');
            return { id: item.id, file: item.file, gcs_path: urlEntry.gcs_path };
          } catch (err: any) {
            console.error(`GCS upload failed for ${item.file.name}:`, err);
            bytesUploadedPerFile.set(item.id, item.file.size);
            failedCount++;
            emitProgress('uploading');

            const status = err?.response?.status;
            const errorMessage =
              status === 400
                ? `Upload rejected (400) for "${item.file.name}" — Content-Type mismatch. Resolved type: "${contentType}".`
                : err?.message || 'GCS upload failed';

            return {
              id: item.id,
              file: item.file,
              gcs_path: null,
              error: errorMessage,
            };
          } finally {
            semaphore.release();
          }
        }),
      );

      const succeeded = gcsResults.filter((r) => r.gcs_path !== null);
      const failed = gcsResults.filter((r) => r.gcs_path === null);

     
      let finalizedFiles: FileAdminDetailWithUser[] = [];

      if (succeeded.length > 0) {
        onBatchProgress({
          phase: 'finalizing',
          uploadedCount,
          totalCount,
          failedCount,
          overallPct: 100,
        });

        try {
          finalizedFiles = [];
          const chunkSize = 100;
          for (let i = 0; i < succeeded.length; i += chunkSize) {
            const chunk = succeeded.slice(i, i + chunkSize);
            const finalizeResponse =
              await clientService.api.batchFinalizeUploadsApiV1AdminFilesFinalizeUploadsPost({
                deployment_id: deploymentId,
                files: chunk.map((r) => ({
                  gcs_path: r.gcs_path!,
                  file_name: r.file.name,
                })),
              });
            finalizedFiles.push(...finalizeResponse.data.files);
          }
        } catch (err) {
          console.error('Batch finalize failed:', err);
          onBatchProgress({
            phase: 'done',
            uploadedCount,
            totalCount,
            failedCount: totalCount,
            overallPct: 100,
          });
          return [
            ...succeeded.map((r) => ({
              id: r.id,
              file: r.file,
              fileRecord: null,
              error:
                'Server finalize failed after GCS upload — files are safely uploaded, please retry or contact support.',
            })),
            ...failed.map((r) => ({
              id: r.id,
              file: r.file,
              fileRecord: null,
              error: r.error,
            })),
          ];
        }
      }

      onBatchProgress({
        phase: 'done',
        uploadedCount,
        totalCount,
        failedCount,
        overallPct: 100,
      });

      const fileRecordByName = new Map<string, FileAdminDetailWithUser>(
        finalizedFiles.map((f) => [f.name, f]),
      );

      return [
        ...succeeded.map((r) => ({
          id: r.id,
          file: r.file,
          fileRecord: fileRecordByName.get(r.file.name) ?? null,
        })),
        ...failed.map((r) => ({
          id: r.id,
          file: r.file,
          fileRecord: null,
          error: r.error,
        })),
      ];
    },
    [],
  );

  return { uploadFiles };
};

export default useFileUpload;